import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Camera, X, Loader2, Copy, Check } from 'lucide-react'
import { supabase } from '@/config/supabase'
import { STORAGE_BUCKETS } from '@/config/constants'
import { buildRoute } from '@/config/routes'
import useBookingStore from '@/stores/bookingStore'
import useAuthStore    from '@/stores/authStore'
import Button from '@/components/ui/Button'

const MAX_PHOTOS    = 5
const MAX_PHOTO_MB  = 10
const TOTAL_STEPS   = 5
const DRAFT_KEY          = 'siglat-description-draft'
const DRAFT_TTL_MS       = 7 * 24 * 60 * 60 * 1000  // 7 days
const REREAD_SESSION_KEY = 'siglat-draft-reread-confirmed'

// ── Draft localStorage helpers ────────────────────────────────────────────────
function saveDraft(text, categorySlug) {
  if (!text.trim()) return
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ text, savedAt: Date.now(), categorySlug }))
  } catch (_) {}
}

function loadDraft(categorySlug) {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return ''
    const { text, savedAt, categorySlug: savedSlug } = JSON.parse(raw)
    if (Date.now() - savedAt > DRAFT_TTL_MS) { localStorage.removeItem(DRAFT_KEY); return '' }
    // Only restore if the draft belongs to this same category
    if (savedSlug && savedSlug !== categorySlug) return ''
    return text ?? ''
  } catch (_) { return '' }
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY) } catch (_) {}
}

function draftDaysLeft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return 0
    const { savedAt } = JSON.parse(raw)
    return Math.max(0, Math.ceil((DRAFT_TTL_MS - (Date.now() - savedAt)) / (24 * 60 * 60 * 1000)))
  } catch (_) { return 0 }
}

// ── Reusable option button ────────────────────────────────────────────────────
function OptionBtn({ label, sublabel, active, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center rounded-xl border px-3 py-3 transition-all text-center
        ${active
          ? danger
            ? 'border-siglat-red bg-siglat-red/10 text-siglat-red'
            : 'border-siglat-teal bg-siglat-teal/10 text-siglat-teal'
          : 'border-surface-border bg-surface-raised text-ink-secondary'
        }`}
    >
      <span className="text-sm font-medium leading-tight">{label}</span>
      {sublabel && <span className="text-xs opacity-70 mt-0.5">{sublabel}</span>}
    </button>
  )
}

// ── Step indicator ────────────────────────────────────────────────────────────
function StepIndicator({ current, total }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300
            ${i < current ? 'bg-siglat-teal flex-1' :
              i === current - 1 ? 'bg-siglat-teal flex-[2]' :
              'bg-surface-border flex-1'}`}
        />
      ))}
    </div>
  )
}

export default function DescribeProblem() {
  const { category: categorySlug } = useParams()
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)
  const store    = useBookingStore()
  const fileInputRef = useRef(null)

  const [category,    setCategory]   = useState(null)
  const [catLoading,  setCatLoading] = useState(true)
  const [submitting,  setSubmitting] = useState(false)
  const [errors,      setErrors]     = useState({})
  const [showBackWarn,  setShowBackWarn]  = useState(false)
  const [copied,        setCopied]        = useState(false)
  const [needsReread,   setNeedsReread]   = useState(false)
  const [showRereadPop, setShowRereadPop] = useState(false)
  const [editUnlocked,  setEditUnlocked]  = useState(false)

  // Load category — always start fresh, then restore draft for this category
  useEffect(() => {
    store.reset()
    const draft = loadDraft(categorySlug)
    if (draft) {
      store.setDescription(draft)
      // Gate textarea — popup only opens on user interaction (tap/Next), not on arrive
      const confirmed = sessionStorage.getItem(REREAD_SESSION_KEY)
      if (!confirmed) {
        setNeedsReread(true)
        setEditUnlocked(false)
      }
    }

    async function fetch() {
      const { data } = await supabase
        .from('service_categories')
        .select('id, name, name_fil, icon, is_active')
        .order('sort_order', { ascending: true })

      if (!data) { navigate('/book'); return }
      const match = data.find(
        (c) => c.name.toLowerCase().replace(/\s+/g, '-') === categorySlug
      )
      if (!match || !match.is_active) { navigate('/book'); return }

      setCategory(match)
      store.setCategory({ id: match.id, name: match.name, slug: categorySlug })
      setCatLoading(false)
    }
    fetch()
  }, [categorySlug]) // eslint-disable-line

  // Intercept browser back button — route it through handleBack()
  // so steps 2-5 go to previous step instead of leaving the page
  useEffect(() => {
    // Push a dummy history entry so back button has something to intercept
    window.history.pushState({ step: store.currentStep }, '')

    function onPopState() {
      // Re-push so back button always has an entry to intercept
      window.history.pushState({ step: store.currentStep }, '')
      handleBack()
    }

    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [store.currentStep]) // eslint-disable-line

  // Pre-fill address
  useEffect(() => {
    if (profile?.address && !store.address) store.setAddress(profile.address)
  }, [profile]) // eslint-disable-line

  // ── Validation per step ───────────────────────────────────────────────────
  function validateStep(step) {
    const errs = {}
    if (step === 1) {
      if (!store.description.trim() || store.description.trim().length < 20)
        errs.description = 'Please describe the problem (at least 20 characters)'
      if (!store.address.trim())
        errs.address = 'Service address is required'
    }
    return errs
  }

  function handleNext() {
    // If user hasn't re-read their restored draft — gate before advancing
    if (needsReread) { setShowRereadPop(true); return }
    const errs = validateStep(store.currentStep)
    if (Object.keys(errs).length) { setErrors(errs); return }
    setErrors({})
    store.nextStep()
  }

  function confirmReread() {
    sessionStorage.setItem(REREAD_SESSION_KEY, '1')
    setNeedsReread(false)
    setEditUnlocked(true)
    setShowRereadPop(false)
  }

  function declineReread() {
    setShowRereadPop(false)
    // Stay on the same page — user dismissed to keep reading
  }

  function handleBack() {
    setErrors({})
    if (store.currentStep === 1) {
      if (store.description.trim().length > 0) {
        setShowBackWarn(true)  // show warning popup first
        return
      }
      navigate('/book')
      return
    }
    store.prevStep()
  }

  function confirmLeave() {
    setShowBackWarn(false)
    navigate('/book')
  }

  function handleCopyDraft() {
    navigator.clipboard.writeText(store.description).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // ── Photo handling ────────────────────────────────────────────────────────
  function handlePhotoSelect(e) {
    const files = Array.from(e.target.files ?? [])
    const remaining = MAX_PHOTOS - store.photos.length
    files.slice(0, remaining).forEach((file) => {
      if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
        setErrors((p) => ({ ...p, photos: 'Each photo must be under ' + MAX_PHOTO_MB + 'MB' }))
        return
      }
      store.addPhoto(file)
    })
    e.target.value = ''
  }

  // ── Final submit ──────────────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitting(true)
    try {
      const fullDescription = store.buildFullDescription()

      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          customer_id:   profile.id,
          category_id:   store.categoryId,
          description:   fullDescription,
          urgency:       store.urgency,
          address:       store.address.trim(),
          address_notes: store.addressNotes.trim() || null,
          status:        'pending',
        })
        .select('id, ref_number')
        .single()

      if (bookingError) throw bookingError
      store.setBookingId(booking.id, booking.ref_number)

      // Notify customer + admin
      supabase.functions.invoke('send-notification', { body: {
        type: 'booking_received', customer_id: profile.id,
        data: { ref_number: booking.ref_number, address: store.address.trim(), category: store.categoryName },
      }}).catch(console.error)
      supabase.functions.invoke('send-notification', { body: {
        type: 'admin_new_booking', customer_id: profile.id,
        data: { ref_number: booking.ref_number, address: store.address.trim(), category: store.categoryName },
      }}).catch(console.error)

      // Upload photos
      for (const photo of store.photos) {
        const ext  = photo.file.name.split('.').pop()
        const path = profile.id + '/' + booking.id + '/' + Date.now() + '.' + ext
        const { error: uploadError } = await supabase.storage
          .from(STORAGE_BUCKETS.BOOKING_PHOTOS)
          .upload(path, photo.file, { upsert: false })
        if (uploadError) { console.error('Photo upload:', uploadError.message); continue }
        await supabase.from('booking_photos').insert({
          booking_id: booking.id, uploaded_by: profile.id,
          photo_type: 'customer_submitted', storage_path: path,
        })
      }

      clearDraft()
      sessionStorage.removeItem(REREAD_SESSION_KEY)
      navigate(buildRoute.bookQuote(categorySlug))
    } catch (err) {
      console.error('Submit error:', err)
      setErrors({ submit: 'Something went wrong. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  if (catLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface-base">
        <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
      </div>
    )
  }

  const step = store.currentStep

  return (
    <div className="min-h-dvh bg-surface-base flex flex-col">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top shrink-0">
        <div className="page-container">
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink-secondary mb-3 -ml-1"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 1 ? 'Back' : 'Previous'}
          </button>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-2xl">{category.icon}</span>
            <div>
              <h1 className="font-display text-xl font-bold text-ink-primary">{category.name}</h1>
              <p className="text-xs text-ink-muted">Step {step} of {TOTAL_STEPS}</p>
            </div>
          </div>
          <StepIndicator current={step} total={TOTAL_STEPS} />
        </div>
      </div>

      {/* Form content */}
      <div className="flex-1 overflow-y-auto">
        <div className="page-container py-5 flex flex-col gap-4">

          {/* ── STEP 1 — Problem Description ─────────────────────────────── */}
          {step === 1 && (
            <>
              <div>
                <p className="font-display text-lg font-bold text-ink-primary mb-1">
                  Describe the problem
                </p>
                <p className="text-sm text-ink-muted">Be as detailed as possible — this helps us give you an accurate estimate.</p>
              </div>

              <div className="flex flex-col gap-1.5">
                {/* Edit chip — only shown after re-read confirmed */}
                {editUnlocked && (
                  <div className="flex items-center gap-1.5 text-xs text-siglat-teal font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-siglat-teal inline-block" />
                    Editing — your previous description is loaded
                  </div>
                )}
                <div className="relative">
                  <textarea
                    value={store.description}
                    readOnly={needsReread}
                    onClick={() => { if (needsReread) setShowRereadPop(true) }}
                    onChange={(e) => {
                      if (needsReread) return
                      store.setDescription(e.target.value)
                      saveDraft(e.target.value, categorySlug)
                      setErrors((p) => ({ ...p, description: null }))
                    }}
                    placeholder="e.g. May tumatak na gripo sa banyo sa 2nd floor. Hindi na matibag, lumalabas ang tubig sa base ng gripo..."
                    rows={5}
                    className={`w-full bg-surface-overlay border rounded-xl px-4 py-3 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal resize-none transition-colors
                      ${needsReread ? 'opacity-70 cursor-pointer select-none border-surface-border' : errors.description ? 'border-siglat-red' : 'border-surface-border'}`}
                  />
                  {/* Frozen overlay hint */}
                  {needsReread && (
                    <div
                      onClick={() => setShowRereadPop(true)}
                      className="absolute inset-0 flex items-center justify-center rounded-xl cursor-pointer"
                    >
                      <span className="bg-surface-raised border border-surface-border rounded-lg px-3 py-1.5 text-xs font-medium text-ink-muted">
                        Tap to re-read before editing
                      </span>
                    </div>
                  )}
                </div>
                {errors.description && <p className="text-xs text-siglat-red">⚠ {errors.description}</p>}
                <p className="text-xs text-ink-muted text-right">{store.description.length} chars</p>
              </div>

              {/* Urgency */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">How urgent?</label>
                <div className="grid grid-cols-2 gap-2">
                  <OptionBtn label="Scheduled" sublabel="Standard rate"
                    active={store.urgency === 'scheduled'} onClick={() => store.setUrgency('scheduled')} />
                  <OptionBtn label="Urgent" sublabel="+30% surcharge" danger
                    active={store.urgency === 'urgent'} onClick={() => store.setUrgency('urgent')} />
                </div>
              </div>

              {/* Address */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink-secondary">
                  Service address <span className="text-siglat-red">*</span>
                </label>
                <input
                  type="text"
                  value={store.address}
                  onChange={(e) => { store.setAddress(e.target.value); setErrors((p) => ({ ...p, address: null })) }}
                  placeholder="123 Mabini St, Brgy. San Jose, Quezon City"
                  className={`w-full bg-surface-overlay border rounded-xl px-4 py-3 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal
                    ${errors.address ? 'border-siglat-red' : 'border-surface-border'}`}
                />
                {errors.address && <p className="text-xs text-siglat-red">⚠ {errors.address}</p>}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink-secondary">Additional directions (optional)</label>
                <input
                  type="text"
                  value={store.addressNotes}
                  onChange={(e) => store.setAddressNotes(e.target.value)}
                  placeholder="e.g. 2nd floor, knock twice, gate code 1234"
                  className="w-full bg-surface-overlay border border-surface-border rounded-xl px-4 py-3 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal"
                />
              </div>

              {/* Photos */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-ink-secondary">
                    Photos <span className="text-ink-muted font-normal">(optional, up to {MAX_PHOTOS})</span>
                  </label>
                  <span className="text-xs text-ink-muted">{store.photos.length}/{MAX_PHOTOS}</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {store.photos.map((photo, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-surface-border">
                      <img src={photo.previewUrl} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => store.removePhoto(idx)}
                        className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                  {store.photos.length < MAX_PHOTOS && (
                    <button onClick={() => fileInputRef.current?.click()}
                      className="w-20 h-20 rounded-lg border-2 border-dashed border-surface-border flex flex-col items-center justify-center gap-1 text-ink-muted hover:border-siglat-teal hover:text-siglat-teal transition-colors">
                      <Camera className="w-5 h-5" />
                      <span className="text-xs">Add</span>
                    </button>
                  )}
                </div>
                {errors.photos && <p className="text-xs text-siglat-red">⚠ {errors.photos}</p>}
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoSelect} />
              </div>
            </>
          )}

          {/* ── STEP 2 — Location & Access ───────────────────────────────── */}
          {step === 2 && (
            <>
              <div>
                <p className="font-display text-lg font-bold text-ink-primary mb-1">Location &amp; Access</p>
                <p className="text-sm text-ink-muted">Help our team plan the job properly.</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink-secondary">Where exactly is the problem?</label>
                <input type="text" value={store.locationInStructure}
                  onChange={(e) => store.setLocationInStructure(e.target.value)}
                  placeholder="e.g. master bedroom ceiling, kitchen sink, 2nd floor CR"
                  className="w-full bg-surface-overlay border border-surface-border rounded-xl px-4 py-3 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal" />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Height from floor</label>
                <div className="grid grid-cols-2 gap-2">
                  {[['ground', 'Ground level', '0-2m'], ['3-4m', '3–4 meters', 'Single story'], ['4-6m', '4–6 meters', 'Two story'], ['6m+', '6+ meters', 'High ceiling']].map(([val, label, sub]) => (
                    <OptionBtn key={val} label={label} sublabel={sub}
                      active={store.heightFromFloor === val} onClick={() => store.setHeightFromFloor(val)} />
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Is scaffolding needed?</label>
                <div className="grid grid-cols-3 gap-2">
                  {[['yes', 'Yes'], ['no', 'No'], ['not_sure', 'Not sure']].map(([val, label]) => (
                    <OptionBtn key={val} label={label}
                      active={store.scaffoldingNeeded === val} onClick={() => store.setScaffoldingNeeded(val)} />
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink-secondary">Access notes (optional)</label>
                <input type="text" value={store.accessNotes}
                  onChange={(e) => store.setAccessNotes(e.target.value)}
                  placeholder="e.g. gate code, elevator access, no parking available"
                  className="w-full bg-surface-overlay border border-surface-border rounded-xl px-4 py-3 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal" />
              </div>
            </>
          )}

          {/* ── STEP 3 — Demolition & Restoration ───────────────────────── */}
          {step === 3 && (
            <>
              <div>
                <p className="font-display text-lg font-bold text-ink-primary mb-1">Demolition &amp; Restoration</p>
                <p className="text-sm text-ink-muted">Will any breaking or removal be involved?</p>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Demolition involved?</label>
                <div className="grid grid-cols-3 gap-2">
                  {[['yes', 'Yes'], ['no', 'No'], ['not_sure', 'Not sure']].map(([val, label]) => (
                    <OptionBtn key={val} label={label}
                      active={store.demolitionInvolved === val} onClick={() => store.setDemolitionInvolved(val)} />
                  ))}
                </div>
              </div>

              {store.demolitionInvolved === 'yes' && (
                <>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-ink-secondary">Elements affected (select all)</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[['ceiling', 'Ceiling'], ['wall', 'Wall'], ['floor_tiles', 'Floor tiles'], ['floor_slab', 'Floor slab'], ['cabinetry', 'Cabinetry'], ['partition', 'Partition'], ['other', 'Other']].map(([val, label]) => (
                        <OptionBtn key={val} label={label}
                          active={store.demolitionElements.includes(val)}
                          onClick={() => store.toggleDemolitionElement(val)} />
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-ink-secondary">Approximate area (optional)</label>
                    <input type="text" value={store.demolitionArea}
                      onChange={(e) => store.setDemolitionArea(e.target.value)}
                      placeholder="e.g. 5 sqm, 3m x 2m"
                      className="w-full bg-surface-overlay border border-surface-border rounded-xl px-4 py-3 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal" />
                  </div>
                </>
              )}

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Restoration needed after?</label>
                <div className="grid grid-cols-3 gap-2">
                  {[['yes', 'Yes'], ['no', 'No'], ['not_sure', 'Not sure']].map(([val, label]) => (
                    <OptionBtn key={val} label={label}
                      active={store.restorationNeeded === val} onClick={() => store.setRestorationNeeded(val)} />
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Paint restoration needed?</label>
                <div className="grid grid-cols-3 gap-2">
                  {[['yes', 'Yes'], ['no', 'No'], ['not_sure', 'Not sure']].map(([val, label]) => (
                    <OptionBtn key={val} label={label}
                      active={store.paintRestoration === val} onClick={() => store.setPaintRestoration(val)} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── STEP 4 — Work Hours ──────────────────────────────────────── */}
          {step === 4 && (
            <>
              <div>
                <p className="font-display text-lg font-bold text-ink-primary mb-1">Work Hours</p>
                <p className="text-sm text-ink-muted">When can our team work at your property?</p>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Noise allowed</label>
                <div className="grid grid-cols-2 gap-2">
                  {[['all_day', 'All day', '8AM–8PM'], ['morning', 'Morning only', '8AM–12PM'], ['afternoon', 'Afternoon only', '1PM–5PM'], ['night', 'Night only', '8PM–12AM']].map(([val, label, sub]) => (
                    <OptionBtn key={val} label={label} sublabel={sub}
                      active={store.noiseAllowed === val} onClick={() => store.setNoiseAllowed(val)} />
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Work hours</label>
                <div className="grid grid-cols-1 gap-2">
                  {[['regular', 'Regular hours (8AM–5PM)', ''], ['extended', 'Extended hours', 'Beyond 5PM'], ['night_shift', 'Night shift required', 'After 8PM']].map(([val, label, sub]) => (
                    <OptionBtn key={val} label={label} sublabel={sub || undefined}
                      active={store.workHours === val} onClick={() => store.setWorkHours(val)} />
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Is the space occupied during works?</label>
                <div className="grid grid-cols-2 gap-2">
                  {[['yes', 'Yes', 'People present'], ['no', 'No', 'Vacated']].map(([val, label, sub]) => (
                    <OptionBtn key={val} label={label} sublabel={sub}
                      active={store.spaceOccupied === val} onClick={() => store.setSpaceOccupied(val)} />
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink-secondary">Other restrictions (optional)</label>
                <input type="text" value={store.workRestrictions}
                  onChange={(e) => store.setWorkRestrictions(e.target.value)}
                  placeholder="e.g. no drilling on Sundays, HOA approval needed"
                  className="w-full bg-surface-overlay border border-surface-border rounded-xl px-4 py-3 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal" />
              </div>
            </>
          )}

          {/* ── STEP 5 — Permits ─────────────────────────────────────────── */}
          {step === 5 && (
            <>
              <div>
                <p className="font-display text-lg font-bold text-ink-primary mb-1">Permits</p>
                <p className="text-sm text-ink-muted">Some buildings require work permits before we can start.</p>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Work permit required from building admin?</label>
                <div className="grid grid-cols-3 gap-2">
                  {[['yes', 'Yes'], ['no', 'No'], ['not_sure', 'Not sure']].map(([val, label]) => (
                    <OptionBtn key={val} label={label}
                      active={store.permitRequired === val} onClick={() => store.setPermitRequired(val)} />
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-ink-secondary">Who handles permitting?</label>
                <div className="grid grid-cols-1 gap-2">
                  {[['yes', 'I will handle it', ''], ['no', 'I cannot handle it', ''], ['sk_handles', 'Siglat Konstract to handle', 'May involve additional fees'], ['not_sure', 'Not sure yet', '']].map(([val, label, sub]) => (
                    <OptionBtn key={val} label={label} sublabel={sub || undefined}
                      active={store.clientHandlesPermit === val} onClick={() => store.setClientHandlesPermit(val)} />
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink-secondary">Additional permit notes (optional)</label>
                <input type="text" value={store.permitNotes}
                  onChange={(e) => store.setPermitNotes(e.target.value)}
                  placeholder="e.g. HOA requires 3-day notice, permit already approved"
                  className="w-full bg-surface-overlay border border-surface-border rounded-xl px-4 py-3 text-ink-primary placeholder-ink-muted text-base focus:outline-none focus:border-siglat-teal" />
              </div>

              {errors.submit && (
                <p className="text-sm text-siglat-red text-center bg-siglat-red/10 rounded-xl px-3 py-2">
                  {errors.submit}
                </p>
              )}

              <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
                <p className="text-xs text-ink-muted leading-relaxed">
                  By submitting, you agree that all materials will be supplied by Siglat Konstract.
                  This is a Scope Estimate only — final pricing is confirmed after site inspection.
                </p>
              </div>
            </>
          )}

        </div>
      </div>

      {/* ── Re-read confirmation popup ───────────────────────────────────── */}
      {showRereadPop && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative w-full max-w-lg bg-surface-raised rounded-t-2xl px-5 pt-5 pb-8 safe-bottom flex flex-col gap-4">
            <div className="w-10 h-1 bg-surface-border rounded-full mx-auto -mt-1 mb-1" />
            <div>
              <p className="font-display text-base font-bold text-ink-primary mb-1">
                Re-read your description first
              </p>
              <p className="text-sm text-ink-muted leading-relaxed">
                You have a saved description from a previous session. Please re-read it carefully before continuing — this helps ensure your estimate is accurate and nothing is missing.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={confirmReread}
                className="w-full py-3 rounded-xl bg-siglat-teal text-surface-base text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Yes, I've re-read it — continue editing
              </button>
              <button
                onClick={declineReread}
                className="w-full py-3 rounded-xl border border-surface-border text-sm font-medium text-ink-secondary hover:bg-surface-overlay transition-colors"
              >
                I'll keep reading — close this
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Back warning popup ───────────────────────────────────────────── */}
      {showBackWarn && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          {/* Scrim */}
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowBackWarn(false)} />
          {/* Sheet */}
          <div className="relative w-full max-w-lg bg-surface-raised rounded-t-2xl px-5 pt-5 pb-8 safe-bottom flex flex-col gap-4">
            <div className="w-10 h-1 bg-surface-border rounded-full mx-auto -mt-1 mb-1" />
            <div>
              <p className="font-display text-base font-bold text-ink-primary mb-1">
                Leave without submitting?
              </p>
              <p className="text-sm text-ink-muted leading-relaxed">
                Your description is saved for <span className="text-siglat-teal font-medium">{draftDaysLeft()} more days</span>. It will be restored when you return to any service category.
              </p>
              <p className="text-xs text-ink-muted mt-2">
                To keep a permanent copy, paste it in your notes app.
              </p>
            </div>

            {/* Copy button */}
            <button
              onClick={handleCopyDraft}
              className="flex items-center justify-center gap-2 w-full border border-surface-border rounded-xl py-3 text-sm font-medium text-ink-secondary hover:border-siglat-teal hover:text-siglat-teal transition-colors"
            >
              {copied
                ? <><Check className="w-4 h-4 text-green-400" /> Copied!</>
                : <><Copy className="w-4 h-4" /> Copy my description</>
              }
            </button>

            <div className="flex gap-2">
              <button
                onClick={() => setShowBackWarn(false)}
                className="flex-1 py-3 rounded-xl border border-surface-border text-sm font-medium text-ink-secondary hover:bg-surface-overlay transition-colors"
              >
                Stay
              </button>
              <button
                onClick={confirmLeave}
                className="flex-1 py-3 rounded-xl bg-siglat-red/10 border border-siglat-red/30 text-sm font-medium text-siglat-red hover:bg-siglat-red/20 transition-colors"
              >
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom navigation */}
      <div className="shrink-0 bg-surface-raised border-t border-surface-border px-4 py-4 safe-bottom">
        <div className="page-container flex flex-col gap-2">
          {step < TOTAL_STEPS ? (
            <Button fullWidth size="lg" onClick={handleNext} rightIcon={<ChevronRight className="w-4 h-4" />}>
              Next
            </Button>
          ) : (
            <Button fullWidth size="lg" loading={submitting} onClick={handleSubmit}>
              {submitting ? 'Submitting\u2026' : 'Get My Scope Estimate \u2192'}
            </Button>
          )}
          {step > 1 && (
            <Button fullWidth variant="ghost" size="md" onClick={handleBack} leftIcon={<ChevronLeft className="w-4 h-4" />}>
              Back
            </Button>
          )}
        </div>
      </div>

    </div>
  )
}
